import React, { useState, useEffect, useRef } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend, Label } from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#8dd1e1'];
const RADIAN = Math.PI / 180;
const SMALL_PERCENTAGE_THRESHOLD = 1; // Porcentaje mínimo para mostrar como sector individual

const BudgetPieChart = ({ posts, budgetData, formatCurrency }) => {
  const { budget } = budgetData;
  const [activeIndex, setActiveIndex] = useState(null);
  const chartRef = useRef(null);

  // Calcular el costo total y el presupuesto restante
  const totalCost = posts.reduce((sum, post) => sum + parseFloat(post.costo), 0);
  const remainingBudget = Math.max(budget - totalCost, 0);

  // Preparar datos para el gráfico circular
  let data = posts.map(post => ({
    name: post.obra,
    value: parseFloat(post.costo)
  }));

  // Agrupar gastos pequeños en "Otros"
  let otherValue = 0;
  data = data.reduce((acc, item) => {
    const percentage = (item.value / budget) * 100;
    if (percentage < SMALL_PERCENTAGE_THRESHOLD) {
      otherValue += item.value;
    } else {
      acc.push(item);
    }
    return acc;
  }, []);

  if (otherValue > 0) {
    data.push({ name: 'Otros', value: otherValue });
  }

  // Añadir presupuesto restante si existe
  if (remainingBudget > 0) {
    data.push({
      name: 'Presupuesto Restante',
      value: remainingBudget
    });
  }

  // Calcular porcentajes
  data = data.map(item => ({
    ...item,
    percentage: ((item.value / budget) * 100).toFixed(2)
  }));

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="custom-tooltip" style={{ backgroundColor: '#fff', padding: '10px', border: '1px solid #ccc' }}>
          <p className="label">{`${data.name}`}</p>
          <p className="info">{`${formatCurrency(data.value)} (${data.percentage}% del total)`}</p>
        </div>
      );
    }
    return null;
  };

  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        style={{
          fontSize: '12px',
          fontWeight: 'bold',
          textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
        }}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  const onPieEnter = (_, index) => {
    setActiveIndex(index);
  };

  const onPieLeave = () => {
    setActiveIndex(null);
  };

  const onLegendEnter = (_, index) => {
    setActiveIndex(index);
  };

  const onLegendLeave = () => {
    setActiveIndex(null);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (chartRef.current && !chartRef.current.contains(event.target)) {
        setActiveIndex(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div style={{ width: '100%', height: 400 }} ref={chartRef}>
      <ResponsiveContainer>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={renderCustomizedLabel}
            outerRadius={150}
            fill="#8884d8"
            dataKey="value"
            onMouseEnter={onPieEnter}
            onMouseLeave={onPieLeave}
          >
            {data.map((entry, index) => (
              <Cell 
                key={`cell-${index}`} 
                fill={COLORS[index % COLORS.length]}
                opacity={activeIndex === null || activeIndex === index ? 1 : 0.6}
                stroke={activeIndex === index ? '#000' : 'none'}
                strokeWidth={2}
              />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend 
            formatter={(value, entry, index) => (
              <span 
                style={{ 
                  color: activeIndex === index ? COLORS[index % COLORS.length] : '#666', 
                  fontWeight: activeIndex === index ? 'bold' : 'normal' 
                }}
                onMouseEnter={() => onLegendEnter(entry, index)}
                onMouseLeave={onLegendLeave}
              >
                {`${value} (${data[index].percentage}%)`}
              </span>
            )}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

export default BudgetPieChart;

